import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CLIENT_ID, LOGIN_ENDPOINT, REFRESH_GRANT_TYPE, SERVER_DOMAIN } from '../../config/ap';
import { RootState } from '../store';
import { removeSessionCredentialsToStorage, setSessionCredentialsToStorage } from '../action/auth-action';
import { RefreshPayload, SessionCredential } from '../../model/auth-model';
import { setSession } from '../slice/auth-slice';

const StatusCode = {
    OK: 200,
    UNAUTHORIZED: 401,
}

const BASE_QUERY = fetchBaseQuery({
    baseUrl: SERVER_DOMAIN,
});

const apiForPublic = createApi({
    reducerPath: 'apiPublic',
    baseQuery: BASE_QUERY,
    endpoints: () => ({}),
});

const BASE_QUERY_AUTH = fetchBaseQuery({
    baseUrl: SERVER_DOMAIN,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.session.accessToken;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args:any, api:any, extraOptions:any) => {

    let result = await BASE_QUERY_AUTH(args, api, extraOptions);
    
    const status = result?.error?.status;

    if (status === StatusCode.UNAUTHORIZED) {
        
        const token: string | null = (api.getState() as RootState).auth.session.refreshToken;
        const params: RefreshPayload = {
            client_id: CLIENT_ID,
            grant_type: REFRESH_GRANT_TYPE,
            refresh_token: token!,
        };

        // LOGIN_ENDPOINT.AUTH is an absolute URL on the Keycloak host.
        const url = LOGIN_ENDPOINT.AUTH;
        const response: any = await refreshToken(url, params, api);

        if (response.status === StatusCode.UNAUTHORIZED) {
            api.dispatch(removeSessionCredentialsToStorage());
            window.location.href = '/login?timeOut=true';
        } else {
            const token = await response.json();
            const session: SessionCredential = {
                accessToken: token.access_token,
                refreshToken: token.refresh_token
            }
            api.dispatch(setSessionCredentialsToStorage(session));
            api.dispatch(setSession(session));
            
            result = await BASE_QUERY_AUTH(args, api, extraOptions);
        }
    }

  return result;
};

// For api calls that require authorization header
const apiForPrivate = createApi({
    reducerPath: 'apiPrivate',
    baseQuery: baseQueryWithReauth,
    endpoints: () => ({}),
});

const apiForText = createApi({
    reducerPath: 'api',
    baseQuery: async (args, api, extraOptions) => {
        const rawResponse = await fetchBaseQuery({
            baseUrl: `${SERVER_DOMAIN}`,
            prepareHeaders: (headers, { getState }) => {
                const token = (getState() as RootState).auth.session.accessToken;
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                }
                headers.set('Content-Type', 'text/plain');
                return headers;
            },
            responseHandler: async (response) => {
                const text = await response.text();
                return text;
            },
        }) (args, api, extraOptions);
            return rawResponse;
        },
        endpoints: () => ({}),
    });

    const refreshToken = async (url: string, body: RefreshPayload, api: any) => {

    let formData = new URLSearchParams();
    formData.append('grant_type', body.grant_type);
    formData.append('client_id', body.client_id);
    formData.append('refresh_token', body.refresh_token);
    
    let header = {
        headers: {'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
        method: 'POST'
    };

    var loginURI = url;
    try {
        let request = new Request(loginURI, header);
        let response = await fetch(request);
        if (response.status !== StatusCode.OK) {
            api.dispatch(removeSessionCredentialsToStorage());
            window.location.href = '/login?timeOut=true';
        } else {
            return response;
        }
    } catch (e) {
        api.dispatch(removeSessionCredentialsToStorage());
        window.location.href = '/login?timeOut=true';
    }
}

export { apiForPublic, apiForPrivate, apiForText }